import React from 'react';
import PolicyPageLayout, {
  PolicySection, Para, BulletList, BulletItem, SubHead, InfoBox, ContactCard,
} from '../components/policy/PolicyPageLayout';

const toc = [
  { id: 'intro',        label: 'Introduction' },
  { id: 'info-collect', label: 'Information We Collect' },
  { id: 'how-we-use',   label: 'How We Use Information' },
  { id: 'what-we-dont', label: "What We Don't Do" },
  { id: 'security',     label: 'Data Security' },
  { id: 'retention',    label: 'Data Retention' },
  { id: 'third-parties',label: 'Third-Party Services' },
  { id: 'your-rights',  label: 'Your Rights' },
  { id: 'dpdp',         label: 'DPDP Act 2023' },
  { id: 'gdpr',         label: 'GDPR — EU Users' },
  { id: 'children',     label: "Children's Privacy" },
  { id: 'changes',      label: 'Policy Changes' },
  { id: 'contact',      label: 'Contact' },
];
const related = [
  { label: 'Terms of Service',          href: '/terms-of-service' },
  { label: 'Cookie Policy',             href: '/cookie-policy' },
  { label: 'Data Processing Agreement', href: '/data-processing-agreement' },
  { label: 'Acceptable Use Policy',     href: '/acceptable-use-policy' },
];

const PrivacyPolicyPage: React.FC = () => (
  <PolicyPageLayout title="Privacy Policy" lastUpdated="April 26, 2026" toc={toc} related={related}>

    <PolicySection id="intro" number="1" title="Introduction">
      <Para>CruvzMail ("we", "our", or "us") operates the CruvzMail email hosting platform at cruvzmail.com, operated by Cruvz Online Shopping India Pvt Ltd. This Privacy Policy explains how we collect, use, store, and protect your personal information.</Para>
      <Para>We are committed to protecting your privacy. As a self-hosted email platform, we design our services so that your data stays under your control — not used for advertising, analytics, or profiling.</Para>
    </PolicySection>

    <PolicySection id="info-collect" number="2" title="Information We Collect">
      <Para>We collect information in the following categories:</Para>
      <SubHead>2.1 Account Information</SubHead>
      <BulletList>
        <BulletItem>Name, email address, and organization name provided during registration</BulletItem>
        <BulletItem>Domain name(s) you register with the platform</BulletItem>
        <BulletItem>Billing information — processed securely via Razorpay; we do not store card details</BulletItem>
        <BulletItem>Account credentials — passwords are hashed with bcrypt and never stored in plain text</BulletItem>
      </BulletList>
      <SubHead>2.2 Email and Communication Data</SubHead>
      <BulletList>
        <BulletItem>Email messages, attachments, contacts, calendar events, tasks, and notes stored within your mailbox</BulletItem>
        <BulletItem>Email metadata (sender, recipient, subject, timestamps) necessary for email delivery</BulletItem>
      </BulletList>
      <SubHead>2.3 Technical Data</SubHead>
      <BulletList>
        <BulletItem>IP addresses and browser/device information for security and session management</BulletItem>
        <BulletItem>Server access logs for troubleshooting and performance monitoring (retained 180 days — CERT-In Directions 2022)</BulletItem>
        <BulletItem>Aggregated, non-personally-identifiable usage analytics</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="how-we-use" number="3" title="How We Use Your Information">
      <Para>We use collected information solely for the following purposes:</Para>
      <BulletList>
        <BulletItem>Providing, maintaining, and improving the CruvzMail platform</BulletItem>
        <BulletItem>Authenticating your identity and managing your account</BulletItem>
        <BulletItem>Processing email delivery on your behalf</BulletItem>
        <BulletItem>Providing customer support and responding to enquiries</BulletItem>
        <BulletItem>Detecting and preventing spam, abuse, and security threats</BulletItem>
        <BulletItem>Processing billing and subscription payments via Razorpay</BulletItem>
        <BulletItem>Sending service-related notifications (account alerts, maintenance updates)</BulletItem>
        <BulletItem>Complying with legal obligations under Indian law (IT Act 2000, DPDP Act 2023, CERT-In Directions 2022)</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="what-we-dont" number="4" title="What We Do NOT Do">
      <InfoBox>CruvzMail is built on the principle of privacy by design. Your email content is yours — we do not monetise it in any way.</InfoBox>
      <Para>We explicitly do not:</Para>
      <BulletList>
        <BulletItem>Scan or read your emails for advertising purposes</BulletItem>
        <BulletItem>Sell, rent, or share your personal data with third-party advertisers</BulletItem>
        <BulletItem>Display advertisements of any kind within the platform</BulletItem>
        <BulletItem>Use your email content for training machine learning models without your explicit consent</BulletItem>
        <BulletItem>Track your activity across other websites</BulletItem>
        <BulletItem>Share personal data with any third party except as described in Section 7</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="security" number="5" title="Data Storage and Security">
      <Para>Your data is stored on our dedicated, self-hosted infrastructure in India. We implement the following security measures:</Para>
      <BulletList>
        <BulletItem>TLS 1.2+ encryption for all data in transit (email transmission and web access)</BulletItem>
        <BulletItem>JWT-based authentication with 15-minute access tokens and 7-day refresh tokens</BulletItem>
        <BulletItem>Two-factor authentication (2FA/TOTP) support for all accounts</BulletItem>
        <BulletItem>bcrypt password hashing — never plaintext or weak hashing</BulletItem>
        <BulletItem>Automated daily backups with 7-day rolling retention</BulletItem>
        <BulletItem>SPF, DKIM, and DMARC compliance for email authentication</BulletItem>
        <BulletItem>Rspamd-powered spam and phishing protection</BulletItem>
        <BulletItem>ClamAV virus scanning on all DriveX file uploads</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="retention" number="6" title="Data Retention">
      <Para>We retain your account data for as long as your account is active. Deleted emails move to Trash and are permanently purged after <strong>30 days</strong>. Server and access logs are retained for <strong>180 days</strong> as required by CERT-In Directions 2022.</Para>
      <Para>If you close your account, all personal data is deleted within <strong>30 days</strong>, except where retention is legally required — for example, billing records are kept for <strong>7 years</strong> under the GST Act.</Para>
    </PolicySection>

    <PolicySection id="third-parties" number="7" title="Third-Party Services">
      <Para>We use only the following third-party services, and no others:</Para>
      <BulletList>
        <BulletItem><strong>Razorpay</strong> — Payment processing. PCI-DSS compliant; we do not store your card information.</BulletItem>
        <BulletItem><strong>Let's Encrypt</strong> — SSL/TLS certificate issuance for encrypted connections.</BulletItem>
        <BulletItem><strong>GoDaddy</strong> — DNS record management for domain verification and DKIM signing (via API scoped to DNS only).</BulletItem>
      </BulletList>
      <Para>We do not share your email content or personal data with any third party beyond what is strictly necessary for the specific function described above.</Para>
    </PolicySection>

    <PolicySection id="your-rights" number="8" title="Your Rights">
      <Para>You have the following rights regarding your personal data:</Para>
      <BulletList>
        <BulletItem><strong>Access:</strong> Request a copy of the personal data we hold about you</BulletItem>
        <BulletItem><strong>Correction:</strong> Request correction of inaccurate personal data</BulletItem>
        <BulletItem><strong>Deletion:</strong> Request deletion of your account and all associated data</BulletItem>
        <BulletItem><strong>Export:</strong> Export your emails, contacts, and calendar data at any time via the admin panel</BulletItem>
        <BulletItem><strong>Restriction:</strong> Request that we restrict processing in certain circumstances</BulletItem>
        <BulletItem><strong>Objection:</strong> Object to processing based on our legitimate interests</BulletItem>
      </BulletList>
      <Para>To exercise any of these rights, contact us at <strong>privacy@cruvzmail.com</strong>. We will respond within 30 days.</Para>
    </PolicySection>

    <PolicySection id="dpdp" number="9" title="DPDP Act 2023 — India">
      <Para>CruvzMail complies with India's Digital Personal Data Protection Act 2023 (DPDP Act):</Para>
      <BulletList>
        <BulletItem><strong>Lawful basis:</strong> We process your personal data on the basis of consent given at registration, or as necessary for the performance of our service agreement</BulletItem>
        <BulletItem><strong>Data Fiduciary:</strong> CruvzMail acts as Data Fiduciary for data collected directly from you. For organizational email data, your organization is the Data Fiduciary</BulletItem>
        <BulletItem><strong>Data Localization:</strong> All data is stored in India on infrastructure operated by Cruvz Online Shopping India Pvt Ltd</BulletItem>
        <BulletItem><strong>Children's data:</strong> We do not knowingly collect data from users under 18 without verifiable parental consent</BulletItem>
        <BulletItem><strong>Grievance Officer:</strong> Contact our designated Grievance Officer at <strong>grievance@cruvzmail.com</strong></BulletItem>
        <BulletItem><strong>Data Protection Board:</strong> You may escalate unresolved grievances to the Data Protection Board of India</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="gdpr" number="10" title="GDPR — European Union">
      <Para>For users in the European Economic Area (EEA), the General Data Protection Regulation (GDPR) applies:</Para>
      <BulletList>
        <BulletItem><strong>Lawful basis:</strong> Contract (Art. 6(1)(b)), consent (Art. 6(1)(a)), and legitimate interests (Art. 6(1)(f)) for security and fraud prevention</BulletItem>
        <BulletItem><strong>Your GDPR rights:</strong> Access (Art. 15), rectification (Art. 16), erasure (Art. 17), restriction (Art. 18), portability (Art. 20), and objection (Art. 21)</BulletItem>
        <BulletItem><strong>Data transfers:</strong> Data is stored in India. EU-to-India transfers are governed by Standard Contractual Clauses (SCCs, Art. 46(2)(c)) — contact legal@cruvzmail.com for signed SCCs</BulletItem>
        <BulletItem><strong>Supervisory authority:</strong> You may lodge a complaint with your local EU data protection supervisory authority</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="children" number="11" title="Children's Privacy">
      <Para>CruvzMail is not directed at children under 13 (or under 18 under DPDP Act 2023). We do not knowingly collect personal information from children without verifiable parental consent. If you believe a child has provided us personal data, contact privacy@cruvzmail.com and we will delete it promptly.</Para>
    </PolicySection>

    <PolicySection id="changes" number="12" title="Changes to This Policy">
      <Para>We may update this Privacy Policy to reflect changes in our practices, technology, or legal requirements. Registered users will be notified of material changes via email or in-platform notice. The "Last updated" date at the top reflects the most recent revision. Continued use constitutes acceptance.</Para>
    </PolicySection>

    <PolicySection id="contact" number="13" title="Contact Us">
      <Para>If you have questions or concerns about this Privacy Policy or our data practices:</Para>
      <ContactCard>
        <strong>Privacy &amp; Data Protection:</strong> privacy@cruvzmail.com<br />
        <strong>Grievance Officer (DPDP Act 2023):</strong> grievance@cruvzmail.com<br />
        <strong>Legal / SCC Requests:</strong> legal@cruvzmail.com<br />
        <strong>General Support:</strong> support@cruvzmail.com<br />
        <br />
        <strong>CruvzMail</strong> — operated by Cruvz Online Shopping India Pvt Ltd<br />
        cruvzmail.com
      </ContactCard>
    </PolicySection>

  </PolicyPageLayout>
);

export default PrivacyPolicyPage;
